import { ObjectManager } from '../../../concepts/objects';
import { Meeting } from './Meeting';
import { Phase } from './Phase';
import { SetupPhaseModel } from './SetupPhaseModel';

const resolveActivePhase = (meeting: Meeting) => meeting?.children[meeting.activePhase]?.as(Phase) ?? null;

const updatePhaseFromModel = (phase: Phase, model: SetupPhaseModel) => {
    phase.key = model.key;
    phase.name = model.name;
    phase.time = model.time * 60 * 1000;
    phase.tools = Object.entries(model.tools).filter(x => x[1]).map(x => x[0]);
    phase.incognito = model.incognito;
    phase.concealment = model.concealment;
    return phase;
};

const updateMeetingTimeFromPhaseModel = (meeting: Meeting, model: SetupPhaseModel) => {
    meeting.timeBank = (model.time - (model.timeUsed ?? 0)) * 60 * 1000;
    meeting.timeStarted = Date.now();
};

export const resolveActiveMeeting = (objects: ObjectManager) => {
    return Array.from(objects.getAll(Meeting))
        .sort((a, b) => a.id.localeCompare(b.id))
        .find(x => !!x.alive) || null;
};

export const resolveActiveMeetingAndPhase = (objects: ObjectManager) => {
    const meeting = resolveActiveMeeting(objects);
    const phase = resolveActivePhase(meeting);
    return [meeting, phase] as const;
};

export const findAndUpdatePhase = (objects: ObjectManager, model: SetupPhaseModel) => {
    const phase = objects.find(Phase, x => x.key === model.key);
    if (!phase) return false;
    objects.update(() => {
        updatePhaseFromModel(phase, model);
        // If this phase is the active phase, update the meeting timebox
        const [activeMeeting, activePhase] = resolveActiveMeetingAndPhase(objects);
        if (phase === activePhase) {
            updateMeetingTimeFromPhaseModel(activeMeeting, model);
        }
    });
    return true;
};

export const createAndStartPhase = (objects: ObjectManager, model: SetupPhaseModel) => {
    const meeting = resolveActiveMeeting(objects);
    if (!meeting) return false;
    objects.update(u => {
        const phase = updatePhaseFromModel(u.create(Phase), model);
        meeting.add(phase);
        meeting.activePhase = meeting.children.length - 1;
        updateMeetingTimeFromPhaseModel(meeting, model);
    });
    return true;
};

export const updateMeetingTime = (objects: ObjectManager, timeMs: number, andStart = true) => {
    const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
    if (!meeting) return false;
    objects.update(() => {
        if (phase) phase.time = timeMs;
        meeting.timeBank = phase.time;
        if (meeting.timePaused) meeting.timePaused = Date.now();
        else if (andStart) meeting.timeStarted = Date.now();
    });
    return true;
};

export const startMeetingTime = (objects: ObjectManager) => {
    const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
    if (!meeting) return false;
    objects.update(() => {
        if (phase) phase.time = 0;
        meeting.timeBank = 0;
        meeting.timeStarted = Date.now();
        meeting.timePaused = null;
    });
    return true;
};

export const stopMeetingTime = (objects: ObjectManager) => {
    const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
    if (!meeting) return false;
    objects.update(() => {
        if (phase) phase.time = 0;
        meeting.timeBank = 0;
        meeting.timeStarted = 0;
        meeting.timePaused = 0;
    });
    return true;
};
